import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Checkbox } from '../../../components/ui/checkbox';
import {
  Calculator,
  Copy,
  Eye,
  Trash2,
  MoreHorizontal,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';

interface Recipe {
  id: string;
  title: string;
  servings: number | null;
  difficulty: string | null;
  cuisine: string | null;
  heroImageUrl: string | null;
  isCustom: boolean;
  origin: 'custom' | 'saved' | 'system';
  ingredientCount: number;
}

interface RecipeManagerTableProps {
  recipes: Recipe[];
  selectedRecipes: string[];
  onSelectRecipe: (recipeId: string, selected: boolean) => void;
  onSelectAll: (selected: boolean) => void;
  onScale: (recipe: Recipe) => void;
  onDuplicate: (recipeId: string) => void;
  onDelete: (recipeId: string) => void;
  onView: (recipeId: string) => void;
}

/**
 * Recipe Manager Table Component
 * 
 * Displays recipes in a professional table format with:
 * - Multi-select checkboxes
 * - Quick actions (scale, duplicate, view, delete)
 * - Visual indicators for recipe type
 */
export function RecipeManagerTable({
  recipes,
  selectedRecipes,
  onSelectRecipe,
  onSelectAll,
  onScale,
  onDuplicate,
  onDelete,
  onView,
}: RecipeManagerTableProps) {
  const allSelected = recipes.length > 0 && selectedRecipes.length === recipes.length;
  const someSelected = selectedRecipes.length > 0 && !allSelected;

  const getOriginBadge = (origin: string, isCustom: boolean) => {
    if (isCustom) {
      return (
        <Badge variant="outline" className="bg-sage/10 text-sage border-sage/20">
          Custom
        </Badge>
      );
    }
    if (origin === 'saved') {
      return (
        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
          Saved
        </Badge>
      );
    }
    return null;
  };

  if (recipes.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <p className="text-lg font-medium text-martinique mb-2">
          No recipes found
        </p>
        <p className="text-sm text-muted-foreground">
          Try adjusting your filters or create a new recipe
        </p>
      </div>
    );
  }

  return (
    <div className="border rounded-lg overflow-hidden bg-white">
      <Table>
        <TableHeader>
          <TableRow className="bg-cream/50">
            <TableHead className="w-12">
              <Checkbox
                checked={allSelected}
                onCheckedChange={onSelectAll}
                aria-label="Select all recipes"
                className={someSelected ? 'data-[state=checked]:bg-coral/50' : ''}
              />
            </TableHead>
            <TableHead className="w-16">Image</TableHead>
            <TableHead>Title</TableHead>
            <TableHead className="w-24">Servings</TableHead>
            <TableHead className="w-28">Difficulty</TableHead>
            <TableHead className="w-32">Cuisine</TableHead>
            <TableHead className="w-24">Type</TableHead>
            <TableHead className="w-32 text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {recipes.map((recipe) => {
            const isSelected = selectedRecipes.includes(recipe.id);
            
            return (
              <TableRow
                key={recipe.id}
                className={isSelected ? 'bg-coral/5' : 'hover:bg-cream/30'}
              >
                <TableCell>
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={(checked) =>
                      onSelectRecipe(recipe.id, checked as boolean)
                    }
                    aria-label={`Select ${recipe.title}`}
                  />
                </TableCell>
                <TableCell>
                  {recipe.heroImageUrl ? (
                    <img
                      src={recipe.heroImageUrl}
                      alt={recipe.title}
                      className="w-12 h-12 object-cover rounded"
                    />
                  ) : (
                    <div className="w-12 h-12 bg-cream rounded flex items-center justify-center">
                      <span className="text-xs text-muted-foreground">
                        No img
                      </span>
                    </div>
                  )}
                </TableCell>
                <TableCell className="font-medium">
                  <button
                    onClick={() => onView(recipe.id)}
                    className="text-left hover:text-coral transition-colors"
                  >
                    {recipe.title}
                  </button>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {recipe.ingredientCount} ingredients
                  </p>
                </TableCell>
                <TableCell>{recipe.servings || '—'}</TableCell>
                <TableCell>
                  {recipe.difficulty ? (
                    <Badge variant="outline" className="text-xs">
                      {recipe.difficulty}
                    </Badge>
                  ) : (
                    '—'
                  )}
                </TableCell>
                <TableCell>{recipe.cuisine || '—'}</TableCell>
                <TableCell>
                  {getOriginBadge(recipe.origin, recipe.isCustom)}
                </TableCell>
                <TableCell>
                  <div className="flex items-center justify-end gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onScale(recipe)}
                      title="Scale Recipe"
                      className="h-8 w-8 p-0"
                    >
                      <Calculator className="h-4 w-4" />
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 w-8 p-0"
                        >
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => onView(recipe.id)}>
                          <Eye className="mr-2 h-4 w-4" />
                          View
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => onDuplicate(recipe.id)}>
                          <Copy className="mr-2 h-4 w-4" />
                          Duplicate
                        </DropdownMenuItem>
                        {recipe.isCustom && (
                          <DropdownMenuItem
                            onClick={() => onDelete(recipe.id)}
                            className="text-red-600 focus:text-red-600"
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
